import { FC } from "react";
import { Button, Group, Modal, MultiSelect, Stack } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { enrollTeam } from "../../../../openapi/api-client";

interface Team {
  id: string;
  name: string;
}

interface EnrollTeamsModalProps {
  opened: boolean;
  onClose: () => void;
  competitionId: string;
  availableTeams: Team[];
}

export const EnrollTeamsModal: FC<EnrollTeamsModalProps> = ({
  opened,
  onClose,
  competitionId,
  availableTeams,
}) => {
  const queryClient = useQueryClient();

  const form = useForm<{ teamIds: string[] }>({
    initialValues: {
      teamIds: [],
    },
  });

  const enrollTeamMutation = useMutation({
    mutationFn: enrollTeam,
  });

  const handleSubmit = form.onSubmit(async (values) => {
    await Promise.all(
      values.teamIds.map((teamId) =>
        enrollTeamMutation.mutateAsync({ id: competitionId, teamId }),
      ),
    );
    void queryClient.invalidateQueries({
      queryKey: ["competition", { competitionId }],
    });
    onClose();
    form.reset();
  });

  const handleClose = () => {
    onClose();
    form.reset();
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title="Mannschaften zum Turnier hinzufügen"
    >
      <form onSubmit={handleSubmit}>
        <Stack>
          <MultiSelect
            label="Mannschaften auswählen"
            placeholder="Mannschaften auswählen"
            data={availableTeams.map((t) => ({
              label: t.name,
              value: t.id,
            }))}
            searchable
            nothingFoundMessage="Keine passende Mannschaft gefunden"
            {...form.getInputProps("teamIds")}
          />

          <Group justify="flex-end">
            <Button variant="default" onClick={handleClose}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              loading={enrollTeamMutation.isPending}
              disabled={form.values.teamIds.length === 0}
            >
              Hinzufügen
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
};
